import React, { useState, useEffect } from "react";
import { Form, Input, Button, Select, Row, Col, Spin } from "antd";
import { useHistory, useParams } from "react-router-dom";
import { getDomainById, updateDomainById } from "@api/email/users/domain";
import { EmailNotificationModal } from "@components";

const { Option } = Select;
const { TextArea } = Input;
const { Password } = Input;

const EmailDomainUpdateForm = () => {
  const [form] = Form.useForm();
  const history = useHistory();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);

  // Notification modal states
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [warningModalVisible, setWarningModalVisible] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [infoModalVisible, setInfoModalVisible] = useState(false);
  const [infoMessage, setInfoMessage] = useState("");

  // Modal handling functions
  const showSuccessModal = (message) => {
    setSuccessModalVisible(true);
  };

  const showErrorModal = (message) => {
    setErrorMessage(message);
    setErrorModalVisible(true);
  };

  const showWarningModal = (message) => {
    setWarningMessage(message);
    setWarningModalVisible(true);
  };

  const showInfoModal = (message) => {
    setInfoMessage(message);
    setInfoModalVisible(true);
  };

  // Fetch domain data on component mount
  useEffect(() => {
    const fetchDomain = async () => {
      try {
        const response = await getDomainById(id);
        const domainData = response.data;
        form.setFieldsValue({
          domainName: domainData.domainName,
          description: domainData.description,
          status: domainData.status,
          adminEmail: domainData.adminEmail,
          // Don't set password - for security reasons
        });
      } catch (error) {
        console.error("Error fetching domain:", error);
        showErrorModal(
          error.response?.data?.message || "Failed to fetch domain details"
        );
      } finally {
        setInitializing(false);
      }
    };

    fetchDomain();
  }, [id, form]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      // Only include password in update if it was changed
      const updateData = { ...values };
      if (!updateData.adminPassword) {
        delete updateData.adminPassword;
      }

      await updateDomainById(id, updateData);
      setLoading(false);
      showSuccessModal("Domain updated successfully");

      // Give time for the modal to be visible before redirecting
      setTimeout(() => {
        history.push("/super-admin/email/domain");
      }, 1500);
    } catch (error) {
      console.error("Error updating domain:", error);
      setLoading(false);
      showErrorModal(
        error.response?.data?.message || "Failed to update domain"
      );
    }
  };

  // Domain validation
  const validateDomainName = (_, value) => {
    const domainRegex =
      /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

    if (!value) {
      return Promise.reject("Please input domain name");
    }

    if (value.length > 253) {
      return Promise.reject("Domain name is too long (max 253 characters)");
    }

    if (value.split(".").some((part) => part.length > 63)) {
      return Promise.reject(
        "Each part of the domain cannot exceed 63 characters"
      );
    }

    if (!domainRegex.test(value)) {
      return Promise.reject(
        "Please enter a valid domain name (e.g., example.com or sub.example.com)"
      );
    }

    return Promise.resolve();
  };

  // Email validation
  const validateEmail = (_, value) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!value) {
      return Promise.reject("Please input admin email");
    }
    if (!emailRegex.test(value)) {
      return Promise.reject("Please enter a valid email address");
    }
    return Promise.resolve();
  };

  // Simplified Password validation (optional for update)
  const validatePassword = (_, value) => {
    if (!value) {
      return Promise.resolve(); // Password is optional in update
    }
    if (value.length < 6) {
      return Promise.reject("Password must be at least 6 characters long");
    }
    return Promise.resolve();
  };

  if (initializing) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Domain Name"
              name="domainName"
              rules={[
                { required: true, message: "Please input domain name" },
                { validator: validateDomainName },
              ]}
              tooltip="Enter a valid domain name (e.g., example.com or sub.example.com)"
              extra="Subdomains are allowed (e.g., blog.example.com, 1.example.com)"
            >
              <Input
                placeholder="e.g., example.com or sub.example.com"
                autoComplete="off"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Status"
              name="status"
              rules={[{ required: true, message: "Please select status" }]}
            >
              <Select>
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Admin Email"
              name="adminEmail"
              rules={[
                { required: true, message: "Please input admin email" },
                { validator: validateEmail },
              ]}
              tooltip="Enter admin email address for this domain"
            >
              <Input placeholder="e.g., admin@example.com" autoComplete="off" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Admin Password (Optional)"
              name="adminPassword"
              rules={[{ validator: validatePassword }]}
              tooltip="Leave blank to keep current password, or enter new password"
              extra="Leave empty to keep the current password. New password must be at least 6 characters long."
            >
              <Password
                placeholder="Enter new admin password (optional)"
                autoComplete="new-password"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label="Description"
              name="description"
              rules={[
                {
                  max: 500,
                  message: "Description cannot be longer than 500 characters",
                },
              ]}
              tooltip="Optional description for this domain"
            >
              <TextArea
                rows={4}
                placeholder="Enter a description for this domain (optional)"
                maxLength={500}
                showCount
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24} style={{ textAlign: "right" }}>
            <Button
              type="default"
              style={{ marginRight: 8 }}
              onClick={() => history.push("/super-admin/email/domain")}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{ minWidth: "120px" }}
            >
              Update Domain
            </Button>
          </Col>
        </Row>
      </Form>

      {/* Success Notification Modal */}
      <EmailNotificationModal
        isOpen={successModalVisible}
        onClose={() => setSuccessModalVisible(false)}
        type="success"
        title="Success"
        message="Domain updated successfully"
        autoCloseTime={3000}
      />

      {/* Error Notification Modal */}
      <EmailNotificationModal
        isOpen={errorModalVisible}
        onClose={() => setErrorModalVisible(false)}
        type="error"
        title="Error"
        message={errorMessage}
      />

      {/* Warning Notification Modal */}
      <EmailNotificationModal
        isOpen={warningModalVisible}
        onClose={() => setWarningModalVisible(false)}
        type="warning"
        title="Warning"
        message={warningMessage}
      />

      {/* Info Notification Modal */}
      <EmailNotificationModal
        isOpen={infoModalVisible}
        onClose={() => setInfoModalVisible(false)}
        type="info"
        title="Information"
        message={infoMessage}
      />
    </>
  );
};

export default EmailDomainUpdateForm;
